import { useCallback, useEffect } from 'react';
import { useDebouncedQueryParam } from '../../../hooks';
import {
  ArrayParam,
  BooleanParam,
  createEnumArrayParam,
  DateParam,
  NumericArrayParam,
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { isThursday } from 'date-fns';
import type { AdType, GoogleAdType } from '../types';
import {
  getLastMonday,
  getLastWeekStart,
  getWeekStartOneWeekAgo,
} from '../utils/getMondays';
import { LANGUAGE_OPTIONS, SORTING_OPTIONS, SORTING_OPTIONS_GOOGLE_US, SORTING_OPTIONS_US } from '../constants';
import type { SortingOption } from '../constants';
import { useLocation } from 'react-router-dom';

const langOptArray = LANGUAGE_OPTIONS.map((l) => String(l.value));
type LangType = (typeof langOptArray)[number];

const OrderParam = withDefault(StringParam, 'total_reach_delta:desc');
export const AVAILABLE_NETWORKS = ['facebook', 'google'] as const;
export const AVAILABLE_REGIONS = ['us', 'eu'] as const;
export type NetworkType = (typeof AVAILABLE_NETWORKS)[number];
export type RegionType = (typeof AVAILABLE_REGIONS)[number];
const DEFAULT_NETWORK = AVAILABLE_NETWORKS[0];
const DEFAULT_REGION = AVAILABLE_REGIONS[0];
const getSortingOptions = (network: NetworkType, region: RegionType): SortingOption[] => {
  if (region === 'eu') return SORTING_OPTIONS;
  if (network === 'google') return SORTING_OPTIONS_GOOGLE_US;
  return SORTING_OPTIONS_US;
};
const DEFAULT_ORDERING_BY_REGION: Record<RegionType, string> = {
  eu: SORTING_OPTIONS[0][0],
  us: ['ads_count', 'desc'].join(':'),
};
const getDefaultOrdering = (network: NetworkType, region: RegionType): string => {
  if (region === 'eu') return SORTING_OPTIONS[0][0];
  if (network === 'google') return ['winner_confidence_score', 'desc'].join(':');
  return DEFAULT_ORDERING_BY_REGION.us;
};
const NetworkParam = withDefault(StringParam, DEFAULT_NETWORK);
const RegionParam = withDefault(StringParam, DEFAULT_REGION);
const BranchParam = withDefault(NumericArrayParam, []);
const PageNameParam = withDefault(NumericArrayParam, []);
const CompanyParam = withDefault(NumericArrayParam, []);
const DomainParam = withDefault(ArrayParam, [] as string[]);
const FlowParam = withDefault(ArrayParam, [] as string[]);
const AdTypeParam = withDefault(createEnumArrayParam<AdType>(['image', 'video']), [] as AdType[]);
const LangParam = withDefault(createEnumArrayParam<LangType>(langOptArray), [] as LangType[]);
// Module-level defaults are Mon–Mon (Meta). Google US is corrected to Thu–Thu in an effect below.
const StartDateParam = withDefault(DateParam, getWeekStartOneWeekAgo(false, getLastWeekStart(false)));
const EndDateParam = withDefault(DateParam, getLastWeekStart(false));
const AllExpandedParam = withDefault(BooleanParam, false);
const IsRunningParam = withDefault(BooleanParam, false);
const NewOnlyParam = withDefault(BooleanParam, true);
const IncludeWelltechParam = withDefault(BooleanParam, false);
const AdvertiserIdParam = withDefault(ArrayParam, [] as string[]);
const GoogleAdTypeParam = withDefault(
  createEnumArrayParam<GoogleAdType>(['video', 'image', 'text']),
  [] as GoogleAdType[]
);
const LastShownRecentlyParam = withDefault(BooleanParam, false);

const isNetworkType = (value: string | null): value is NetworkType =>
  !!value && AVAILABLE_NETWORKS.includes(value as NetworkType);

const isRegionType = (value: string | null): value is RegionType =>
  !!value && AVAILABLE_REGIONS.includes(value as RegionType);

const isOrderByForNetworkRegion = (value: string | null, network: NetworkType, region: RegionType): value is string =>
  !!value && getSortingOptions(network, region).some(([optionValue]) => optionValue === value);

const useFacebookAdsLibraryParams = () => {
  const location = useLocation();
  const {
    param: adTypes,
    actualValue: actualAdTypes,
    handleChange: setAdTypes,
  } = useDebouncedQueryParam<typeof AdTypeParam, AdType[]>({ name: 'ad_types', defaultValue: AdTypeParam });

  const {
    param: languages,
    actualValue: actualLanguages,
    handleChange: setLanguages,
  } = useDebouncedQueryParam<typeof LangParam, LangType[]>({ name: 'lang', defaultValue: LangParam });

  const {
    param: branches,
    actualValue: actualBranches,
    handleChange: setBranches,
  } = useDebouncedQueryParam<typeof BranchParam, number[]>({ name: 'branch', defaultValue: BranchParam });

  const {
    param: pageName,
    actualValue: actualPageName,
    handleChange: setPageName,
  } = useDebouncedQueryParam<typeof PageNameParam, number[]>({ name: 'page_name', defaultValue: PageNameParam });

  const {
    param: company,
    actualValue: actualCompany,
    handleChange: setCompany,
  } = useDebouncedQueryParam<typeof CompanyParam, number[]>({ name: 'company', defaultValue: CompanyParam });

  const {
    param: domains,
    actualValue: actualDomains,
    handleChange: setDomains,
  } = useDebouncedQueryParam<typeof DomainParam, string[]>({ name: 'domain', defaultValue: DomainParam });

  const {
    param: flows,
    actualValue: actualFlows,
    handleChange: setFlows,
  } = useDebouncedQueryParam<typeof FlowParam, string[]>({ name: 'flow', defaultValue: FlowParam });

  const {
    param: advertiserIds,
    actualValue: actualAdvertiserIds,
    handleChange: setAdvertiserIds,
  } = useDebouncedQueryParam<typeof AdvertiserIdParam, string[]>({
    name: 'advertiser_id',
    defaultValue: AdvertiserIdParam,
  });

  const {
    param: googleAdTypes,
    actualValue: actualGoogleAdTypes,
    handleChange: setGoogleAdTypes,
  } = useDebouncedQueryParam<typeof GoogleAdTypeParam, GoogleAdType[]>({
    name: 'google_ad_types',
    defaultValue: GoogleAdTypeParam,
  });

  const [lastShownRecently, setLastShownRecently] = useQueryParam('last_shown_recently', LastShownRecentlyParam);

  const [ordering, setOrdering] = useQueryParam('order_by', OrderParam);
  const [network, setNetwork] = useQueryParam('network', NetworkParam);
  const [region, setRegion] = useQueryParam('region', RegionParam);
  const [startDate, setStartDate] = useQueryParam('start_date', StartDateParam);
  const [endDate, setEndDate] = useQueryParam('end_date', EndDateParam);
  const [allExpanded, setAllExpanded] = useQueryParam('all_expanded', AllExpandedParam);
  const [isRunning, setIsRunning] = useQueryParam('running', IsRunningParam);
  const [newOnly, setNewOnly] = useQueryParam('new_only', NewOnlyParam);
  const [includeWelltech, setIncludeWelltech] = useQueryParam('include_welltech', IncludeWelltechParam);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const requestedNetwork = queryParams.get('network');
    const requestedRegion = queryParams.get('region');
    const requestedOrdering = queryParams.get('order_by');
    const validatedNetwork = isNetworkType(requestedNetwork) ? requestedNetwork : DEFAULT_NETWORK;
    const validatedRegion = isRegionType(requestedRegion) ? requestedRegion : DEFAULT_REGION;

    if (!isNetworkType(requestedNetwork)) {
      setNetwork(DEFAULT_NETWORK);
    }

    if (!isRegionType(requestedRegion)) {
      setRegion(validatedRegion);
    }

    if (!isOrderByForNetworkRegion(requestedOrdering, validatedNetwork, validatedRegion)) {
      setOrdering(getDefaultOrdering(validatedNetwork, validatedRegion));
    }
  }, [location.search, setNetwork, setOrdering, setRegion]);

  useEffect(() => {
    if (!isNetworkType(network) || network !== 'google') return;
    if (isThursday(startDate) && isThursday(endDate)) return;

    const pastWeekStart = getLastWeekStart(true);
    setStartDate(getWeekStartOneWeekAgo(true, pastWeekStart));
    setEndDate(pastWeekStart);
  }, [network, startDate, endDate, setStartDate, setEndDate]);

  const [, setAllFilters] = useQueryParams({
    branch: BranchParam,
    company: CompanyParam,
    page_name: PageNameParam,
    domain: DomainParam,
    flow: FlowParam,
    ad_types: AdTypeParam,
    advertiser_id: AdvertiserIdParam,
    google_ad_types: GoogleAdTypeParam,
    order_by: OrderParam,
    network: NetworkParam,
    region: RegionParam,
    start_date: StartDateParam,
    end_date: EndDateParam,
    running: IsRunningParam,
    new_only: NewOnlyParam,
    include_welltech: IncludeWelltechParam,
    last_shown_recently: LastShownRecentlyParam,
  });

  const [, setRegionScopedFilters] = useQueryParams({
    region: RegionParam,
    order_by: OrderParam,
    domain: DomainParam,
    flow: FlowParam,
    running: IsRunningParam,
    new_only: NewOnlyParam,
    include_welltech: IncludeWelltechParam,
    end_date: EndDateParam,
    advertiser_id: AdvertiserIdParam,
    google_ad_types: GoogleAdTypeParam,
    last_shown_recently: LastShownRecentlyParam,
  });

  const [, setNetworkScopedFilters] = useQueryParams({
    network: NetworkParam,
    region: RegionParam,
    order_by: OrderParam,
    branch: BranchParam,
    company: CompanyParam,
    page_name: PageNameParam,
    domain: DomainParam,
    flow: FlowParam,
    ad_types: AdTypeParam,
    advertiser_id: AdvertiserIdParam,
    google_ad_types: GoogleAdTypeParam,
    include_welltech: IncludeWelltechParam,
    last_shown_recently: LastShownRecentlyParam,
    start_date: StartDateParam,
    end_date: EndDateParam,
  });

  const resetParams = useCallback<() => void>(() => {
    const currentNetwork = isNetworkType(network) ? network : DEFAULT_NETWORK;
    const currentRegion = isRegionType(region) ? region : DEFAULT_REGION;
    const useThursdayWeek = currentNetwork === 'google';
    const pastWeekStart = getLastWeekStart(useThursdayWeek);
    setAllFilters({
      branch: [],
      company: [],
      page_name: [],
      domain: [],
      flow: [],
      ad_types: [],
      advertiser_id: [],
      google_ad_types: [],
      order_by: getDefaultOrdering(currentNetwork, currentRegion),
      network: currentNetwork,
      region: currentRegion,
      start_date: getWeekStartOneWeekAgo(useThursdayWeek, pastWeekStart),
      end_date: pastWeekStart,
      running: false,
      new_only: currentRegion === 'us',
      include_welltech: false,
      last_shown_recently: false,
    });
  }, [setAllFilters, network, region]);

  const setNetworkWithScopedFilters = useCallback(
    (nextNetwork: NetworkType) => {
      const nextRegion: RegionType = nextNetwork === 'google' ? 'us' : DEFAULT_REGION;
      const useThursdayWeek = nextNetwork === 'google';
      const pastWeekStart = getLastWeekStart(useThursdayWeek);
      setNetworkScopedFilters({
        network: nextNetwork,
        region: nextRegion,
        order_by: getDefaultOrdering(nextNetwork, nextRegion),
        branch: [],
        company: [],
        page_name: [],
        domain: [],
        flow: [],
        ad_types: [],
        advertiser_id: [],
        google_ad_types: [],
        include_welltech: false,
        last_shown_recently: false,
        // Google US uses Thu–Thu weeks; Meta stays Mon–Mon — reset so calendar values stay valid.
        start_date: getWeekStartOneWeekAgo(useThursdayWeek, pastWeekStart),
        end_date: pastWeekStart,
      });
    },
    [setNetworkScopedFilters]
  );

  const setRegionWithInitializedFilters = useCallback(
    (nextRegion: RegionType) => {
      const currentNetwork = isNetworkType(network) ? network : DEFAULT_NETWORK;
      setRegionScopedFilters({
        region: nextRegion,
        order_by: getDefaultOrdering(currentNetwork, nextRegion),
        domain: [],
        flow: [],
        // Keep region-specific controls in a safe state on region switch.
        running: false,
        new_only: nextRegion === 'us',
        include_welltech: false,
        // Reset End Week to default — different regions have different min-date
        // constraints (see MIN_DATE_BY_REGION), so an old end_date may be invalid.
        // Region switch only applies to Meta (Google is US-only).
        end_date: getLastMonday(),
        advertiser_id: [],
        google_ad_types: [],
        last_shown_recently: false,
      });
    },
    [setRegionScopedFilters, network]
  );

  const safeRegion = isRegionType(region) ? region : DEFAULT_REGION;
  const safeNetwork = isNetworkType(network) ? network : DEFAULT_NETWORK;
  const safeOrdering = isOrderByForNetworkRegion(ordering, safeNetwork, safeRegion)
    ? ordering
    : getDefaultOrdering(safeNetwork, safeRegion);

  return {
    adTypes,
    setAdTypes,
    actualAdTypes,
    languages,
    setLanguages,
    actualLanguages,
    ordering: safeOrdering,
    setOrdering,
    network: safeNetwork,
    setNetwork,
    setNetworkWithScopedFilters,
    region: isRegionType(region) ? region : DEFAULT_REGION,
    setRegion,
    setRegionWithInitializedFilters,
    branches,
    actualBranches,
    setBranches,
    pageName,
    actualPageName,
    setPageName,
    company,
    actualCompany,
    setCompany,
    domains,
    actualDomains,
    setDomains,
    flows,
    actualFlows,
    setFlows,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    allExpanded,
    setAllExpanded,
    isRunning,
    setIsRunning,
    newOnly: !!newOnly,
    setNewOnly,
    includeWelltech: !!includeWelltech,
    setIncludeWelltech,
    advertiserIds,
    actualAdvertiserIds,
    setAdvertiserIds,
    googleAdTypes,
    actualGoogleAdTypes,
    setGoogleAdTypes,
    lastShownRecently: !!lastShownRecently,
    setLastShownRecently,
    resetParams,
  };
};

export default useFacebookAdsLibraryParams;
